import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetDMOInvitationByTokenQuery } from '@/services/DMOManagementService';
import DMORegistrationForm from './components/DMORegistrationForm';
import { useEffect, useState, type ReactNode } from 'react';
import { toAbsoluteUrl } from '@/utils';
import {
  DMORegisterForm,
  DmoInvitationStatus,
} from '@/types/dmo-management.types';

const PublicDMORegistrationLayout = ({ children }: { children: ReactNode }) => (
  <div className="min-h-screen w-full flex flex-col">
    <header className="w-full border-b border-gray-200 bg-white/60">
      <div className="max-w-xl mx-auto px-4 py-4 flex justify-center">
        <img
          src={toAbsoluteUrl('/media/app/logo.svg')}
          alt="Saaristo Admin Logo"
          className="h-8 w-auto invert dark:invert-0"
        />
      </div>
    </header>
    <div className="flex-1 flex items-center justify-center px-4 py-10">
      {children}
    </div>
    <footer className="w-full border-t border-gray-100">
      <div className="max-w-xl mx-auto px-4 py-4 text-center">
        <p className="text-xs text-gray-400">
          &copy; {new Date().getFullYear()} Saaristo. All rights reserved.
        </p>
      </div>
    </footer>
  </div>
);

const PublicDMORegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetDMOInvitationByTokenQuery(
    token as string,
    {
      skip: !token,
    }
  );

  const [initialData, setInitialData] = useState<Partial<DMORegisterForm>>({
    email: '',
    name: '',
    language: 'en',
  });

  useEffect(() => {
    if (data?.data) {
      setInitialData({
        email: data.data.email || '',
        name: data.data.name || '',
        language: 'en',
      });
    }
  }, [data]);

  if (isLoading) {
    return (
      <PublicDMORegistrationLayout>
        <div className="w-full max-w-md">
          <div className="card w-full">
            <div className="card-body text-center py-10">
              <div className="text-base font-medium text-gray-700">
                Loading...
              </div>
            </div>
          </div>
        </div>
      </PublicDMORegistrationLayout>
    );
  }

  if (isError || !data?.data) {
    return (
      <PublicDMORegistrationLayout>
        <div className="w-full max-w-md">
          <div className="card w-full shadow-sm border-none">
            <div className="card-body text-center py-10">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 14c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Invalid Invitation
              </h2>
              <p className="text-sm text-gray-600">
                This invitation link is invalid or has expired. Please contact
                the administrator for a new invitation.
              </p>
            </div>
          </div>
        </div>
      </PublicDMORegistrationLayout>
    );
  }

  const invitationStatus = (data.data as { status?: DmoInvitationStatus })
    .status;

  if (invitationStatus && invitationStatus !== 'active') {
    if (invitationStatus === 'completed') {
      return (
        <PublicDMORegistrationLayout>
          <div className="w-full max-w-md">
            <div className="card w-full shadow-sm border-none">
              <div className="card-body text-center py-10">
                <div className="text-green-500 mb-4">
                  <svg
                    className="w-16 h-16 mx-auto"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Registration Already Completed
                </h2>
                <p className="text-sm text-gray-600 mb-6">
                  This invitation has already been used to complete the
                  registration. Your login credentials have been sent to your
                  email address.
                </p>
                <button
                  className="btn btn-primary w-full justify-center"
                  type="button"
                  onClick={() => navigate('/auth/login')}
                >
                  Go to Login
                </button>
              </div>
            </div>
          </div>
        </PublicDMORegistrationLayout>
      );
    }

    let title = 'Invalid Invitation';
    let description =
      'This invitation link is invalid or has expired. Please contact the administrator for a new invitation.';

    if (invitationStatus === 'expired') {
      title = 'Invitation Expired';
      description =
        'This invitation link has expired. Please contact the administrator to request a new invitation.';
    } else if (invitationStatus === 'cancelled') {
      title = 'Invitation Cancelled';
      description =
        'This invitation has been cancelled by the administrator. Please contact them if you believe this is a mistake.';
    }

    return (
      <PublicDMORegistrationLayout>
        <div className="w-full max-w-md">
          <div className="card w-full shadow-sm border-none">
            <div className="card-body text-center py-10">
              <div className="text-red-500 mb-4">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 14c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {title}
              </h2>
              <p className="text-sm text-gray-600">{description}</p>
            </div>
          </div>
        </div>
      </PublicDMORegistrationLayout>
    );
  }

  return (
    <PublicDMORegistrationLayout>
      <div className="w-full max-w-xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">
            Welcome to Saaristo
          </h1>
          <p className="text-sm text-gray-600">
            Complete your DMO registration to get started.
          </p>
        </div>
        <div className="card w-full">
          <div className="card-body p-6 sm:p-8">
            <DMORegistrationForm
              token={token as string}
              initialData={initialData}
              showToolbar={false}
              showLegalConsent
              onSuccess={() => {
                navigate('/auth/login');
              }}
              onCancel={() => {
                navigate('/auth/login');
              }}
            />
          </div>
        </div>
      </div>
    </PublicDMORegistrationLayout>
  );
};

export default PublicDMORegistrationPage;
